const SCHEMA_NODE_ID_PREFIX = "schema-";
const TABLE_NODE_ID_PREFIX = "table-";
const DATA_SOURCE_NODE_ID_PREFIX = "dataSource-";

/**
 * Canvas graph node ids use `schema-{uuid}` or `table-{uuid}`; drawer APIs
 * expect the raw ConnectorAsset UUID (no prefix).
 */
export function parseSchemaAssetId(canvasOrAssetId: string): string {
  if (canvasOrAssetId.startsWith(SCHEMA_NODE_ID_PREFIX)) {
    return canvasOrAssetId.slice(SCHEMA_NODE_ID_PREFIX.length);
  }
  if (canvasOrAssetId.startsWith(TABLE_NODE_ID_PREFIX)) {
    return canvasOrAssetId.slice(TABLE_NODE_ID_PREFIX.length);
  }
  return canvasOrAssetId;
}

/** Raw connector asset UUID → canvas table node id (`schema-{uuid}`). */
export function toSchemaCanvasNodeId(assetOrSchemaId: string): string {
  return `${SCHEMA_NODE_ID_PREFIX}${parseSchemaAssetId(assetOrSchemaId)}`;
}

/** Raw connector asset UUID → canvas table node id (`table-{uuid}`). */
export function toTableCanvasNodeId(assetOrTableId: string): string {
  return `${TABLE_NODE_ID_PREFIX}${parseSchemaAssetId(assetOrTableId)}`;
}

/**
 * Canvas graph node ids use `dataSource-{uuid}`; drawer APIs accept raw UUID
 * (workspace_connector, connector_asset, or connector_connection id — BE resolves).
 */
export function parseDataSourceAssetId(canvasOrAssetId: string): string {
  return canvasOrAssetId.startsWith(DATA_SOURCE_NODE_ID_PREFIX)
    ? canvasOrAssetId.slice(DATA_SOURCE_NODE_ID_PREFIX.length)
    : canvasOrAssetId;
}

/** Raw data source UUID → canvas node id (`dataSource-{uuid}`). */
export function toDataSourceCanvasNodeId(dataSourceOrCanvasId: string): string {
  return `${DATA_SOURCE_NODE_ID_PREFIX}${parseDataSourceAssetId(dataSourceOrCanvasId)}`;
}

const ONTOLOGY_NODE_ID_PREFIX = "ontology-";

/**
 * Canvas graph node ids use `ontology-{uuid}`; drawer APIs expect raw
 * KGOolDomain UUID (no prefix).
 */
export function parseOntologyDomainId(canvasOrDomainId: string): string {
  return canvasOrDomainId.startsWith(ONTOLOGY_NODE_ID_PREFIX)
    ? canvasOrDomainId.slice(ONTOLOGY_NODE_ID_PREFIX.length)
    : canvasOrDomainId;
}

/** Raw KGOolDomain UUID → canvas ontology node id (`ontology-{uuid}`). */
export function toOntologyCanvasNodeId(domainOrCanvasId: string): string {
  return `${ONTOLOGY_NODE_ID_PREFIX}${parseOntologyDomainId(domainOrCanvasId)}`;
}

const METRIC_NODE_ID_PREFIX = "metric-";

/**
 * Canvas graph node ids use `metric-{uuid}`; drawer APIs expect raw KGMetric UUID.
 */
export function parseMetricAssetId(canvasOrMetricId: string): string {
  return canvasOrMetricId.startsWith(METRIC_NODE_ID_PREFIX)
    ? canvasOrMetricId.slice(METRIC_NODE_ID_PREFIX.length)
    : canvasOrMetricId;
}

/** Raw KGMetric UUID → canvas metric node id (`metric-{uuid}`). */
export function toMetricCanvasNodeId(metricOrCanvasId: string): string {
  return `${METRIC_NODE_ID_PREFIX}${parseMetricAssetId(metricOrCanvasId)}`;
}

const REPORT_NODE_ID_PREFIX = "report-";

export function parseReportAssetId(canvasOrReportId: string): string {
  return canvasOrReportId.startsWith(REPORT_NODE_ID_PREFIX)
    ? canvasOrReportId.slice(REPORT_NODE_ID_PREFIX.length)
    : canvasOrReportId;
}

/** Raw report UUID → canvas report node id (`report-{uuid}`). */
export function toReportCanvasNodeId(reportOrCanvasId: string): string {
  return `${REPORT_NODE_ID_PREFIX}${parseReportAssetId(reportOrCanvasId)}`;
}

const STORY_NODE_ID_PREFIX = "story-";

export function parseStoryAssetId(canvasOrStoryId: string): string {
  return canvasOrStoryId.startsWith(STORY_NODE_ID_PREFIX)
    ? canvasOrStoryId.slice(STORY_NODE_ID_PREFIX.length)
    : canvasOrStoryId;
}

/** Raw story UUID → canvas data story node id (`story-{uuid}`). */
export function toStoryCanvasNodeId(storyOrCanvasId: string): string {
  return `${STORY_NODE_ID_PREFIX}${parseStoryAssetId(storyOrCanvasId)}`;
}

const ARTIFACT_NODE_ID_PREFIX = "artifact-";

export function parseArtifactAssetId(canvasOrArtifactId: string): string {
  return canvasOrArtifactId.startsWith(ARTIFACT_NODE_ID_PREFIX)
    ? canvasOrArtifactId.slice(ARTIFACT_NODE_ID_PREFIX.length)
    : canvasOrArtifactId;
}

/** Raw artifact UUID → canvas artifact node id (`artifact-{uuid}`). */
export function toArtifactCanvasNodeId(artifactOrCanvasId: string): string {
  return `${ARTIFACT_NODE_ID_PREFIX}${parseArtifactAssetId(artifactOrCanvasId)}`;
}

const DATA_MODEL_NODE_ID_PREFIX = "dataModel-";

/**
 * Canvas graph node ids use `dataModel-{uuid}`; drawer APIs expect the raw UUID.
 */
export function parseDataModelAssetId(canvasOrDataModelId: string): string {
  return canvasOrDataModelId.startsWith(DATA_MODEL_NODE_ID_PREFIX)
    ? canvasOrDataModelId.slice(DATA_MODEL_NODE_ID_PREFIX.length)
    : canvasOrDataModelId;
}

/** Raw data model UUID → canvas node id (`dataModel-{uuid}`). */
export function toDataModelCanvasNodeId(dataModelOrCanvasId: string): string {
  return `${DATA_MODEL_NODE_ID_PREFIX}${parseDataModelAssetId(dataModelOrCanvasId)}`;
}

/** Extract `focus` query param from `/graph?focus=...` hrefs (undefined if absent). */
export function parseFocusIdFromHref(hrefOrId: string): string | undefined {
  const match = hrefOrId.match(/[?&]focus=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Sample-graph node ids (ont-*, tbl-*, ds-*, …) are already canvas ids — do not prefix. */
export const isSampleGraphCanvasId = (id: string): boolean =>
  /^(ont-|tbl-|ds-|m-|rpt-|dstory-|dm-|dataSource-|schema-|table-|metric-|ontology-|report-|story-|artifact-|dataModel-)/.test(
    id,
  );

/** Parse a canvas node's KgFlowNodeType from its prefixed id. Returns undefined for unknown prefixes. */
export const parseNodeTypeFromCanvasId = (
  canvasNodeId: string,
): import("../../components/kg/canvas/types").KgFlowNodeType | undefined => {
  if (canvasNodeId.startsWith("dataSource-")) return "dataSource";
  if (canvasNodeId.startsWith("table-")) return "table";
  if (canvasNodeId.startsWith("schema-")) return "schema";
  if (canvasNodeId.startsWith("ontology-")) return "ontology";
  if (canvasNodeId.startsWith("metric-")) return "metric";
  if (canvasNodeId.startsWith("report-")) return "report";
  if (canvasNodeId.startsWith("story-")) return "dataStory";
  if (canvasNodeId.startsWith("artifact-")) return "artifact";
  if (canvasNodeId.startsWith("dataModel-")) return "dataModel";
  return undefined;
};
